import { useState } from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'

import RowComponent from './RowComponent'

import theme from 'theme'
import { track } from 'track'
import CheckBox from 'components/common/CheckBox'
import Button from 'components/common/Button'
import Text from 'components/common/Text'
import Icon from 'components/Icon'
import HaltConfirmation from 'components/attacks/HaltConfirmation'
import ResultsForm from 'components/scenarios/detail/ResultsForm'

export default function AttackRow({
  showHaltButton,
  selectableRow,
  onRowSelected,
  clickableRow,
  onClick,
  checked,
  ...props
}) {
  return (
    <DefaultAttackRow
      checked={checked}
      clickableRow={clickableRow}
      data-track-action="active-attack-notification-row-clicked"
      data-track-category="active-attack"
      data-track-on="click"
      selectableRow={selectableRow}
      showHaltButton={showHaltButton}
      onClick={event => {
        if (onClick) {
          track('ActiveAttackNotificationRowClicked')
          onClick(event)
        }
        return {}
      }}
      onRowSelected={onRowSelected}
      {...props}
    />
  )
}

AttackRow.propTypes = {
  showHaltButton: PropTypes.bool,
  selectableRow: PropTypes.bool,
  onRowSelected: PropTypes.func,
  onClick: PropTypes.func,
  clickableRow: PropTypes.bool,
  checked: PropTypes.bool
}

export function DefaultAttackRow({
  showHaltButton,
  selectableRow,
  onRowSelected,
  clickableRow,
  checked,
  children,
  ...props
}) {
  const handleChange = () => {
    if (onRowSelected) onRowSelected()
  }

  const initialColumn = selectableRow ? `${theme.space[4]}px` : ''
  return (
    <RowComponent
      as={clickableRow ? Link : 'div'}
      css={{
        display: 'grid',
        columnGap: theme.space[3],
        padding: theme.space[3],
        borderBottom: `1px solid ${theme.colors.borderContrast[0]}`,
        '&:hover': {
          color: theme.colors.steel[2],
          background: theme.colors.grayOverlay[0]
        },
        '&:last-of-type': {
          borderBottom: 'none'
        },
        gridTemplateColumns: `${initialColumn}
          ${
            showHaltButton
              ? 'minmax(172px, 1fr) minmax(0, 1fr) 64px 24px'
              : 'minmax(172px, 1fr) minmax(0, 1fr)'
          }
        `
      }}
      {...props}
    >
      {selectableRow && (
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CheckBox checked={checked} onChange={handleChange} />
        </div>
      )}
      {children}
    </RowComponent>
  )
}

DefaultAttackRow.propTypes = {
  showHaltButton: PropTypes.bool,
  selectableRow: PropTypes.bool,
  onRowSelected: PropTypes.func,
  children: PropTypes.node,
  onClick: PropTypes.func,
  clickableRow: PropTypes.bool,
  checked: PropTypes.bool
}

DefaultAttackRow.defaultProps = {
  clickableRow: true,
  onClick: () => {}
}

export function AttackDescription({ icon, name, description }) {
  return (
    <div
      css={{
        display: 'grid',
        columnGap: theme.space[3],
        rowGap: theme.space[1],
        gridTemplateColumns: icon ? '42px 1fr' : '1fr',
        alignItems: 'center'
      }}
    >
      {icon}
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          textAlign: 'left'
        }}
      >
        <Text overflowEllipsis as="h4" color="medium">
          {name}
        </Text>
        <Text
          data-test-attack-description
          as="small"
          css={{ color: theme.colors.steel[1] }}
          weight="normal"
        >
          {description}
        </Text>
      </div>
    </div>
  )
}

AttackDescription.propTypes = {
  icon: PropTypes.node,
  name: PropTypes.node,
  description: PropTypes.node
}

export function Status(props) {
  return <Text data-test-status as="small" weight="normal" {...props} />
}

export function HaltButton({
  fetchAttack,
  title = 'Halt attack',
  trackingOptions,
  trackedEvent = 'SingleAttackHalted',
  type = 'attack',
  showHaltReason = true,
  onConfirmRequest,
  isHalting = false,
  runInfo = {}
}) {
  const [haltConfirmationOpen, setHaltConfirmationOpen] = useState(false)
  const showResultsForm = Boolean(runInfo?.scenario_id)
  return (
    <div
      css={{ position: 'relative', justifySelf: 'center' }}
      onClick={e => {
        e.stopPropagation()
      }}
    >
      <Button
        destructive
        data-track-action="active-attack-notification-halt-clicked"
        data-track-category="active-attack"
        data-track-on="click"
        disabled={isHalting}
        onClick={e => {
          e.preventDefault()
          setHaltConfirmationOpen(true)
        }}
      >
        {isHalting ? ' Halting' : 'Halt'}
      </Button>
      <HaltConfirmation
        doFetch={fetchAttack}
        hideFooter={showResultsForm}
        message={
          showResultsForm ? (
            <ResultsForm
              isConfirmationModal
              doFetch={fetchAttack}
              footerOptions={{
                confirmText: 'Confirm'
              }}
              hideSave={true}
              runInfo={runInfo}
              onCancel={() => setHaltConfirmationOpen(false)}
              onConfirm={onConfirmRequest}
            />
          ) : null
        }
        open={haltConfirmationOpen}
        showConfirmationContent={!showResultsForm}
        showHaltReason={showHaltReason}
        title={title}
        trackedEvent={trackedEvent}
        trackingOptions={trackingOptions}
        type={type}
        onCancel={() => setHaltConfirmationOpen(false)}
        onConfirmRequest={onConfirmRequest}
      />
    </div>
  )
}

HaltButton.propTypes = {
  fetchAttack: PropTypes.func,
  trackingOptions: PropTypes.object,
  title: PropTypes.string,
  trackedEvent: PropTypes.string,
  type: PropTypes.string,
  showHaltReason: PropTypes.bool,
  onConfirmRequest: PropTypes.func,
  onRowSelected: PropTypes.func,
  isHalting: PropTypes.bool,
  runInfo: PropTypes.object
}

export function TargetsColumn({ children }) {
  return (
    <div
      css={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignSelf: 'center'
      }}
    >
      {children}
    </div>
  )
}

TargetsColumn.propTypes = {
  children: PropTypes.node
}

export function DetailsChevron() {
  return (
    <div css={{ display: 'grid', placeItems: 'center' }}>
      <Icon
        css={{
          fontSize: theme.fontSizes[3],
          color: theme.colors.steel[2]
        }}
        icon="chevronForward"
      />
    </div>
  )
}

DetailsChevron.propTypes = {
  children: PropTypes.node
}
